/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import PageBackButton from '../../components/PageBackButton';
import { Tabs, TabPanel } from '../../components/ui/tabs';
import { getSectorAnalytics, getSymbolMetadataOptions, runScreener, searchSymbols } from '../../api/dataWarehouseApi';
import { normalizeSectorOptions } from './sectorOptions';
import SectorVisuals from './SectorVisuals';
import {
  Search, Plus, X, ChevronUp, ChevronDown, Loader2,
  RotateCcw, BarChart3, ChevronLeft, ChevronRight,
  List, Compass, Building2, DollarSign, Coins, Layers, Package,
  Landmark, Columns3, SlidersHorizontal, TrendingUp, TrendingDown,
  Flame, Crown,
} from 'lucide-react';

// ── Helpers ──────────────────────────────────────────────────────

function fmt(val, decimals = 2) {
  if (val == null) return '—';
  const n = Number(val);
  if (Number.isNaN(n)) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function fmtBig(val) {
  if (val == null) return '—';
  const n = Number(val);
  if (Number.isNaN(n)) return '—';
  if (Math.abs(n) >= 1e12) return `${(n / 1e12).toFixed(2)}T`;
  if (Math.abs(n) >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return n.toLocaleString();
}

function fmtPct(val) {
  if (val == null) return '—';
  const n = Number(val);
  if (Number.isNaN(n)) return '—';
  return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
}

function colorForChange(val) {
  if (val == null) return '';
  return Number(val) >= 0 ? 'text-success' : 'text-destructive';
}

// ── Operator labels ──────────────────────────────────────────────
const OPS = [
  { value: 'gt', label: '>' },
  { value: 'gte', label: '>=' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '<=' },
  { value: 'eq', label: '=' },
  { value: 'ne', label: '!=' },
];

// ── Field categories for the filter picker ───────────────────────
const FIELD_CATEGORIES = {
  'Price': ['close', 'open', 'high', 'low', 'change_pct', 'change_abs', 'gap_pct'],
  'Trend': ['sma_20', 'sma_50', 'sma_100', 'sma_200', 'ema_20', 'ema_50', 'price_vs_sma20', 'price_vs_sma50', 'price_vs_sma200'],
  'Momentum': ['rsi_14', 'macd_line', 'macd_signal', 'macd_histogram', 'stoch_k', 'stoch_d', 'adx_14', 'plus_di', 'minus_di', 'cci_14', 'williams_r'],
  'Volatility': ['atr_14', 'bb_upper', 'bb_middle', 'bb_lower', 'bb_width_pct'],
  'Volume': ['volume', 'obv', 'vwap', 'cmf_20', 'mfi_14', 'volume_sma_20', 'volume_ratio'],
  'Price Action': ['high_52w', 'low_52w', 'dist_from_52w_high', 'dist_from_52w_low'],
  'Fundamental': ['market_cap', 'pe_ratio', 'eps', 'dividend_yield', 'revenue', 'profit_margin', 'beta'],
};

function fieldLabel(name) {
  return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// ── Preset filters ───────────────────────────────────────────────
const PRESETS = [
  { label: 'Oversold (RSI < 30)', filters: [{ field: 'rsi_14', op: 'lt', value: 30 }] },
  { label: 'Overbought (RSI > 70)', filters: [{ field: 'rsi_14', op: 'gt', value: 70 }] },
  { label: 'Strong Trend (ADX > 25)', filters: [{ field: 'adx_14', op: 'gt', value: 25 }] },
  { label: 'High Volume', filters: [{ field: 'volume_ratio', op: 'gt', value: 2 }] },
  { label: 'Near 52W High', filters: [{ field: 'dist_from_52w_high', op: 'gt', value: -5 }] },
  { label: 'Golden Cross Possible', filters: [{ field: 'price_vs_sma50', op: 'gt', value: 0 }, { field: 'price_vs_sma200', op: 'gt', value: 0 }] },
  { label: 'Large Cap (>$10B)', filters: [{ field: 'market_cap', op: 'gt', value: 10000000000 }] },
  { label: 'High Dividend (>3%)', filters: [{ field: 'dividend_yield', op: 'gt', value: 3 }] },
];

const MARKET_MOVER_SECTIONS = [
  {
    key: 'gainers',
    title: 'Top Gainers',
    icon: TrendingUp,
    tone: 'text-success',
    filters: [{ field: 'change_pct', op: 'gt', value: 0 }],
    sort: { field: 'change_pct', direction: 'desc' },
    metric: (row) => fmtPct(row.change_pct),
    metricTone: 'text-success',
  },
  {
    key: 'losers',
    title: 'Top Losers',
    icon: TrendingDown,
    tone: 'text-destructive',
    filters: [{ field: 'change_pct', op: 'lt', value: 0 }],
    sort: { field: 'change_pct', direction: 'asc' },
    metric: (row) => fmtPct(row.change_pct),
    metricTone: 'text-destructive',
  },
  {
    key: 'active',
    title: 'Most Active',
    icon: Flame,
    tone: 'text-warning',
    filters: [],
    sort: { field: 'volume_ratio', direction: 'desc' },
    metric: (row) => row.volume_ratio != null ? `${fmt(row.volume_ratio, 1)}x` : fmtBig(row.volume),
    metricTone: 'text-warning',
  },
  {
    key: 'caps',
    title: 'Largest Caps',
    icon: Crown,
    tone: 'text-primary',
    filters: [],
    sort: { field: 'market_cap', direction: 'desc' },
    metric: (row) => row.market_cap ? fmtBig(row.market_cap) : '—',
    metricTone: 'text-primary',
  },
];

// ── Asset class tabs ─────────────────────────────────────────────
const ASSET_CLASSES = [
  { value: '', label: 'All', icon: Compass },
  { value: 'STOCK', label: 'Stocks', icon: Building2 },
  { value: 'FOREX', label: 'Forex', icon: DollarSign },
  { value: 'CRYPTO', label: 'Crypto', icon: Coins },
  { value: 'ETF', label: 'ETFs', icon: Layers },
  { value: 'COMMODITY', label: 'Commodities', icon: Package },
  { value: 'INDEX', label: 'Indices', icon: Landmark },
];

// ── Default visible columns ──────────────────────────────────────
const DEFAULT_COLUMNS = [
  'symbol', 'name', 'sector', 'close', 'change_pct', 'volume', 'rsi_14', 'adx_14',
  'macd_histogram', 'sma_50', 'market_cap',
];

const COLUMN_DEFS = {
  symbol: { label: 'Symbol', align: 'left', width: 'w-24' },
  name: { label: 'Name', align: 'left', width: 'min-w-[140px] max-w-[200px]' },
  sector: { label: 'Sector', align: 'left', width: 'min-w-[120px] max-w-[180px]' },
  industry: { label: 'Industry', align: 'left', width: 'min-w-[140px] max-w-[220px]' },
  close: { label: 'Price', align: 'right', format: v => fmt(v), mono: true },
  open: { label: 'Open', align: 'right', format: v => fmt(v), mono: true },
  high: { label: 'High', align: 'right', format: v => fmt(v), mono: true },
  low: { label: 'Low', align: 'right', format: v => fmt(v), mono: true },
  change_pct: { label: 'Change %', align: 'right', format: v => fmtPct(v), color: true },
  change_abs: { label: 'Change', align: 'right', format: v => fmt(v, 4), color: true },
  volume: { label: 'Volume', align: 'right', format: v => fmtBig(v) },
  rsi_14: { label: 'RSI', align: 'right', format: v => fmt(v, 1) },
  adx_14: { label: 'ADX', align: 'right', format: v => fmt(v, 1) },
  macd_histogram: { label: 'MACD Hist', align: 'right', format: v => fmt(v, 4), color: true },
  macd_line: { label: 'MACD', align: 'right', format: v => fmt(v, 4) },
  macd_signal: { label: 'Signal', align: 'right', format: v => fmt(v, 4) },
  sma_20: { label: 'SMA 20', align: 'right', format: v => fmt(v) },
  sma_50: { label: 'SMA 50', align: 'right', format: v => fmt(v) },
  sma_100: { label: 'SMA 100', align: 'right', format: v => fmt(v) },
  sma_200: { label: 'SMA 200', align: 'right', format: v => fmt(v) },
  ema_20: { label: 'EMA 20', align: 'right', format: v => fmt(v) },
  ema_50: { label: 'EMA 50', align: 'right', format: v => fmt(v) },
  stoch_k: { label: 'Stoch %K', align: 'right', format: v => fmt(v, 1) },
  stoch_d: { label: 'Stoch %D', align: 'right', format: v => fmt(v, 1) },
  cci_14: { label: 'CCI', align: 'right', format: v => fmt(v, 1) },
  williams_r: { label: 'Williams %R', align: 'right', format: v => fmt(v, 1) },
  atr_14: { label: 'ATR', align: 'right', format: v => fmt(v, 4) },
  bb_upper: { label: 'BB Upper', align: 'right', format: v => fmt(v) },
  bb_lower: { label: 'BB Lower', align: 'right', format: v => fmt(v) },
  bb_width_pct: { label: 'BB Width %', align: 'right', format: v => fmt(v, 2) },
  obv: { label: 'OBV', align: 'right', format: v => fmtBig(v) },
  vwap: { label: 'VWAP', align: 'right', format: v => fmt(v) },
  cmf_20: { label: 'CMF', align: 'right', format: v => fmt(v, 4) },
  mfi_14: { label: 'MFI', align: 'right', format: v => fmt(v, 1) },
  volume_ratio: { label: 'Vol Ratio', align: 'right', format: v => fmt(v, 2) },
  price_vs_sma20: { label: 'vs SMA20 %', align: 'right', format: v => fmtPct(v), color: true },
  price_vs_sma50: { label: 'vs SMA50 %', align: 'right', format: v => fmtPct(v), color: true },
  price_vs_sma200: { label: 'vs SMA200 %', align: 'right', format: v => fmtPct(v), color: true },
  high_52w: { label: '52W High', align: 'right', format: v => fmt(v) },
  low_52w: { label: '52W Low', align: 'right', format: v => fmt(v) },
  dist_from_52w_high: { label: '% from 52H', align: 'right', format: v => fmtPct(v), color: true },
  dist_from_52w_low: { label: '% from 52L', align: 'right', format: v => fmtPct(v), color: true },
  gap_pct: { label: 'Gap %', align: 'right', format: v => fmtPct(v), color: true },
  market_cap: { label: 'Mkt Cap', align: 'right', format: v => fmtBig(v) },
  pe_ratio: { label: 'P/E', align: 'right', format: v => fmt(v, 1) },
  eps: { label: 'EPS', align: 'right', format: v => fmt(v, 2) },
  dividend_yield: { label: 'Div Yield %', align: 'right', format: v => fmt(v, 2) },
  revenue: { label: 'Revenue', align: 'right', format: v => fmtBig(v) },
  profit_margin: { label: 'Profit Margin', align: 'right', format: v => fmt(v, 2) },
  beta: { label: 'Beta', align: 'right', format: v => fmt(v, 2) },
};

function MarketMoversPanel({ sections, movers, loading, navigate }) {
  return (
    <Card className="screener-movers-panel rounded-lg">
      <CardContent className="p-3">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-primary" />
            <h2 className="text-sm font-semibold text-textPrimary">Market Movers</h2>
          </div>
          {loading && (
            <span className="inline-flex items-center gap-1 text-xs text-textSecondary">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Updating
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-4">
          {sections.map((section) => {
            const Icon = section.icon;
            const rows = movers[section.key] || [];
            return (
              <div key={section.key} className="screener-mover-group">
                <div className="screener-mover-group-header">
                  <Icon className={`h-3.5 w-3.5 ${section.tone}`} />
                  <span>{section.title}</span>
                </div>
                <div className="screener-mover-list">
                  {rows.length === 0 && (
                    <div className="screener-mover-empty">{loading ? 'Loading...' : 'No results'}</div>
                  )}
                  {rows.slice(0, 3).map((row) => (
                    <button
                      key={`${section.key}-${row.symbol}`}
                      type="button"
                      onClick={() => navigate(`/explorer/${row.symbol}`)}
                      className="screener-mover-row"
                    >
                      <span className="min-w-0">
                        <span className="block truncate font-mono text-xs font-semibold text-primary">{row.symbol}</span>
                        <span className="block truncate text-[11px] text-textSecondary">{row.name || row.sector || '—'}</span>
                      </span>
                      <span className={`font-mono text-xs font-semibold ${section.metricTone}`}>
                        {section.metric(row)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// ══════════════════════════════════════════════════════════════════
// ScreenerPage — Main Component
// ══════════════════════════════════════════════════════════════════

export default function ScreenerPage() {
  const navigate = useNavigate();

  // ── State ──
  const [assetClass, setAssetClass] = useState('STOCK');
  const [sector, setSector] = useState('');
  const [sectorOptions, setSectorOptions] = useState([]);
  const [filters, setFilters] = useState([]);
  const [sort, setSort] = useState({ field: 'symbol', direction: 'asc' });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [results, setResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [columns, setColumns] = useState(DEFAULT_COLUMNS);
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [showFilterBuilder, setShowFilterBuilder] = useState(false);
  const [quickSearch, setQuickSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [activeViewTab, setActiveViewTab] = useState('table');
  const [sectorAnalytics, setSectorAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);
  const [visualPeriod, setVisualPeriod] = useState('1D');
  const [marketMovers, setMarketMovers] = useState({});
  const [marketMoversLoading, setMarketMoversLoading] = useState(true);

  // New filter row state
  const [newField, setNewField] = useState('rsi_14');
  const [newOp, setNewOp] = useState('lt');
  const [newValue, setNewValue] = useState('');

  const totalPages = Math.ceil(totalCount / pageSize);
  const activeFilterCount = filters.length + (sector ? 1 : 0);
  const sectorChoices = normalizeSectorOptions(sectorOptions);
  const selectedAssetClass = ASSET_CLASSES.find(ac => ac.value === assetClass) || ASSET_CLASSES[0];
  const resultLabel = loading ? 'Loading instruments' : `${totalCount.toLocaleString()} instruments`;
  const activeFilterLabel = `${activeFilterCount} ${activeFilterCount === 1 ? 'filter' : 'filters'}`;

  // ── Run screener ──
  const fetchScreener = useCallback(async (overrides = {}) => {
    setLoading(true);
    setError(null);
    try {
      const body = {
        timeframe: 'D1',
        filters: overrides.filters ?? filters,
        asset_class: overrides.asset_class ?? assetClass,
        sector: overrides.sector ?? sector,
        sort: overrides.sort ?? sort,
        page: overrides.page ?? page,
        page_size: pageSize,
      };
      const res = await runScreener(body);
      setResults(res?.results || []);
      setTotalCount(res?.total_count ?? res?.total ?? 0);
    } catch (err) {
      setError(err?.message || 'Failed to load screener data');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [filters, assetClass, sector, sort, page, pageSize]);

  const fetchSectorAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    setAnalyticsError(null);
    try {
      const body = {
        timeframe: 'D1',
        filters,
        asset_class: assetClass,
        sector,
        period: visualPeriod,
      };
      const res = await getSectorAnalytics(body);
      setSectorAnalytics(res);
    } catch (err) {
      setAnalyticsError(err?.message || 'Failed to load sector analytics');
      setSectorAnalytics(null);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [filters, assetClass, sector, visualPeriod]);

  const fetchMarketMovers = useCallback(async () => {
    setMarketMoversLoading(true);
    try {
      const entries = await Promise.all(
        MARKET_MOVER_SECTIONS.map(async (section) => {
          try {
            const res = await runScreener({
              timeframe: 'D1',
              filters: section.filters,
              asset_class: assetClass,
              sector,
              sort: section.sort,
              page: 1,
              page_size: 3,
            });
            return [section.key, res?.results || []];
          } catch {
            return [section.key, []];
          }
        })
      );
      setMarketMovers(Object.fromEntries(entries));
    } finally {
      setMarketMoversLoading(false);
    }
  }, [assetClass, sector]);

  // Initial load + reload on changes
  useEffect(() => {
    fetchScreener();
  }, [fetchScreener]);

  useEffect(() => {
    if (activeViewTab === 'visuals') {
      fetchSectorAnalytics();
    }
  }, [activeViewTab, fetchSectorAnalytics]);

  useEffect(() => {
    if (activeViewTab === 'table') {
      fetchMarketMovers();
    }
  }, [activeViewTab, fetchMarketMovers]);

  // ── Metadata options ──
  useEffect(() => {
    let cancelled = false;

    async function loadSectorOptions() {
      try {
        const params = assetClass ? { asset_class: assetClass } : {};
        const res = await getSymbolMetadataOptions(params);
        if (!cancelled) setSectorOptions(res?.sectors || []);
      } catch {
        if (!cancelled) setSectorOptions([]);
      }
    }

    loadSectorOptions();
    return () => { cancelled = true; };
  }, [assetClass]);

  // ── Quick search ──
  useEffect(() => {
    const q = quickSearch.trim();
    if (q.length < 2) { setSearchResults([]); return; }
    setSearchLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await searchSymbols(q);
        setSearchResults(res?.results || []);
      } catch { setSearchResults([]); }
      finally { setSearchLoading(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [quickSearch]);

  // ── Filter actions ──
  const addFilter = () => {
    const val = parseFloat(newValue);
    if (Number.isNaN(val)) return;
    setFilters(prev => [...prev, { field: newField, op: newOp, value: val }]);
    setNewValue('');
    setPage(1);
  };

  const removeFilter = (idx) => {
    setFilters(prev => prev.filter((_, i) => i !== idx));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters([]);
    setSector('');
    setPage(1);
  };

  const applyPreset = (preset) => {
    setFilters(preset.filters);
    setPage(1);
  };

  const handlePresetChange = (event) => {
    const preset = PRESETS[Number(event.target.value)];
    if (preset) applyPreset(preset);
  };

  // ── Sort toggle ──
  const toggleSort = (field) => {
    setSort(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setPage(1);
  };

  // ── Column toggle ──
  const toggleColumn = (col) => {
    setColumns(prev => prev.includes(col) ? prev.filter(c => c !== col) : [...prev, col]);
  };

  return (
    <div className="my-2 sm:my-4 md:my-6 mx-auto max-w-[100rem] space-y-3">
      <Card className="screener-command-panel rounded-lg overflow-visible">
        <CardContent className="p-4 sm:p-5">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
            <div className="flex min-w-0 items-start gap-3">
              <PageBackButton label="Back to Research" to="/research" preferHistory={false} hideLabelOnMobile />
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-xl sm:text-2xl font-semibold text-textPrimary">Market Screener</h1>
                  <span className="screener-chip screener-chip-muted">D1</span>
                </div>
                <p className="mt-1 max-w-2xl text-sm text-textSecondary">
                  Filter {resultLabel} across technical and fundamental criteria.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="screener-chip">{selectedAssetClass.label}</span>
                  <span className="screener-chip">{activeFilterLabel}</span>
                  <span className="screener-chip">{columns.length} columns</span>
                </div>
              </div>
            </div>

            <div className="relative w-full xl:w-[22rem]">
              <Search className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-textSecondary" />
              <input
                type="text"
                placeholder="Search symbol or name"
                value={quickSearch}
                onChange={e => setQuickSearch(e.target.value)}
                className="input-primary screener-search-input h-10 w-full text-sm"
              />
              {(searchResults.length > 0 || searchLoading) && quickSearch.length >= 2 && (
                <div className="absolute left-0 right-0 top-full z-50 mt-2 max-h-72 overflow-auto rounded-lg border border-borderDefault bg-backgroundSurface shadow-xl">
                  {searchLoading && (
                    <div className="p-3 text-center">
                      <Loader2 className="mx-auto h-4 w-4 animate-spin text-primary" />
                    </div>
                  )}
                  {searchResults.map(s => (
                    <button
                      key={s.symbol}
                      onClick={() => { navigate(`/explorer/${s.symbol}`); setQuickSearch(''); setSearchResults([]); }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition hover:bg-primary/10"
                    >
                      <span className="font-mono font-semibold text-primary">{s.symbol}</span>
                      <span className="truncate text-textSecondary">{s.name}</span>
                      {s.asset_class && <Badge className="ml-auto flex-shrink-0">{s.asset_class}</Badge>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="screener-command-bar mt-4">
            <div className="screener-segmented screener-asset-strip overflow-x-auto">
              {ASSET_CLASSES.map(ac => {
                const Icon = ac.icon;
                return (
                  <button
                    key={ac.value}
                    onClick={() => { setAssetClass(ac.value); setSector(''); setPage(1); }}
                    className={`screener-segment-button ${assetClass === ac.value ? 'screener-segment-button-active' : ''}`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{ac.label}</span>
                  </button>
                );
              })}
            </div>

            <div className="screener-command-tools">
              <label className="screener-select-control">
                <span>Sector</span>
                <select
                  value={sector}
                  onChange={e => { setSector(e.target.value); setPage(1); }}
                  className="screener-select"
                >
                  <option value="">All sectors</option>
                  {sectorChoices.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </label>

              <label className="screener-select-control">
                <span>Preset</span>
                <select
                  value=""
                  onChange={handlePresetChange}
                  className="screener-select"
                >
                  <option value="" disabled>Apply preset</option>
                  {PRESETS.map((preset, index) => (
                    <option key={preset.label} value={index}>{preset.label}</option>
                  ))}
                </select>
              </label>

              <span className="screener-tool-divider" aria-hidden="true" />

              <button
                type="button"
                onClick={() => setShowFilterBuilder(!showFilterBuilder)}
                className={`screener-action-button ${showFilterBuilder ? 'screener-action-button-active' : ''}`}
              >
                <SlidersHorizontal className="h-4 w-4" />
                <span>Filters</span>
                <span className="screener-count">{activeFilterCount}</span>
                {showFilterBuilder ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              <button
                type="button"
                onClick={() => setShowColumnPicker(!showColumnPicker)}
                className={`screener-action-button ${showColumnPicker ? 'screener-action-button-active' : ''}`}
              >
                <Columns3 className="h-4 w-4" />
                <span>Columns</span>
                <span className="screener-count">{columns.length}</span>
                {showColumnPicker ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>

              {activeFilterCount > 0 && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="screener-reset-button"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Clear all
                </button>
              )}
            </div>
          </div>

          {activeFilterCount > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {sector && (
                <span className="screener-filter-chip">
                  <span className="font-medium">Sector</span>
                  <span>=</span>
                  <span>{sector}</span>
                  <button onClick={() => { setSector(''); setPage(1); }} className="hover:text-destructive" aria-label="Remove sector filter">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )}
              {filters.map((f, i) => (
                <span key={i} className="screener-filter-chip">
                  <span className="font-medium">{fieldLabel(f.field)}</span>
                  <span>{OPS.find(o => o.value === f.op)?.label || f.op}</span>
                  <span className="font-mono">{typeof f.value === 'number' && Math.abs(f.value) > 1e6 ? fmtBig(f.value) : f.value}</span>
                  <button onClick={() => removeFilter(i)} className="hover:text-destructive" aria-label={`Remove ${fieldLabel(f.field)} filter`}>
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {showFilterBuilder && (
            <div className="screener-subpanel mt-3">
              <div className="grid gap-3 md:grid-cols-[minmax(12rem,1fr)_7rem_9rem_auto] md:items-end">
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-textSecondary">Field</span>
                  <select value={newField} onChange={e => setNewField(e.target.value)}
                    className="select-primary h-9 w-full text-sm">
                    {Object.entries(FIELD_CATEGORIES).map(([cat, fields]) => (
                      <optgroup key={cat} label={cat}>
                        {fields.map(f => <option key={f} value={f}>{fieldLabel(f)}</option>)}
                      </optgroup>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-textSecondary">Operator</span>
                  <select value={newOp} onChange={e => setNewOp(e.target.value)}
                    className="select-primary h-9 w-full text-sm">
                    {OPS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-textSecondary">Value</span>
                  <Input
                    type="number"
                    value={newValue}
                    onChange={e => setNewValue(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addFilter()}
                    placeholder="0"
                    className="h-9 text-sm"
                  />
                </label>
                <Button size="sm" onClick={addFilter} className="h-9 flex-shrink-0">
                  <Plus className="mr-1 h-4 w-4" /> Add
                </Button>
              </div>
            </div>
          )}

          {showColumnPicker && (
            <div className="screener-subpanel mt-3">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs font-medium text-textSecondary">Visible columns</p>
                <div className="flex gap-2">
                  <button onClick={() => setColumns(DEFAULT_COLUMNS)} className="text-xs text-primary hover:underline">Reset</button>
                  <button onClick={() => setColumns(Object.keys(COLUMN_DEFS))} className="text-xs text-primary hover:underline">Show all</button>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(COLUMN_DEFS).filter(([key]) => key !== 'symbol').map(([key, def]) => (
                  <button
                    key={key}
                    onClick={() => toggleColumn(key)}
                    className={`screener-column-chip ${columns.includes(key) ? 'screener-column-chip-active' : ''}`}
                  >
                    {def.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {activeViewTab === 'table' && (
        <MarketMoversPanel
          sections={MARKET_MOVER_SECTIONS}
          movers={marketMovers}
          loading={marketMoversLoading}
          navigate={navigate}
        />
      )}

      <div className="screener-tabs-shell">
        <Tabs
          tabs={[
            { key: 'table', label: 'Table', icon: <List className="w-4 h-4" /> },
            { key: 'visuals', label: 'Visuals', icon: <BarChart3 className="w-4 h-4" /> },
          ]}
          activeTab={activeViewTab}
          onChange={setActiveViewTab}
          className="border-b-0"
        />
        <div className="hidden text-xs text-textSecondary md:block">
          {resultLabel}
        </div>
      </div>

      <TabPanel tabKey="table" activeTab={activeViewTab}>
        <Card className="rounded-lg overflow-hidden">
          <CardContent className="p-0">
            {loading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-primary mr-2" />
                <span className="text-textSecondary">Loading...</span>
              </div>
            )}

            {error && !loading && (
              <div className="text-center py-12">
                <p className="text-destructive mb-2">{error}</p>
                <Button variant="outline" size="sm" onClick={() => fetchScreener()}>Retry</Button>
              </div>
            )}

            {!loading && !error && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="screener-table-head">
                      {columns.filter(c => COLUMN_DEFS[c]).map(col => {
                        const def = COLUMN_DEFS[col];
                        const isSorted = sort.field === col;
                        const sortable = !['name', 'sector', 'industry'].includes(col);
                        return (
                          <th key={col}
                            onClick={() => sortable && toggleSort(col)}
                            className={`py-2.5 px-3 text-xs font-semibold text-textSecondary whitespace-nowrap ${def.align === 'right' ? 'text-right' : 'text-left'} ${def.width || ''} ${sortable ? 'cursor-pointer hover:text-primary select-none' : ''}`}
                          >
                            <span className="inline-flex items-center gap-1">
                              {def.label}
                              {isSorted && (sort.direction === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                            </span>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {results.length === 0 && (
                      <tr><td colSpan={columns.length} className="text-center py-12 text-textSecondary">No results. Adjust your filters or asset class.</td></tr>
                    )}
                    {results.map((row, i) => (
                      <tr key={row.symbol || i}
                        onClick={() => navigate(`/explorer/${row.symbol}`)}
                        className="screener-table-row cursor-pointer">
                        {columns.filter(c => COLUMN_DEFS[c]).map(col => {
                          const def = COLUMN_DEFS[col];
                          const val = row[col];

                          // Special rendering for symbol
                          if (col === 'symbol') {
                            return (
                              <td key={col} className="py-2.5 px-3">
                                <span className="font-mono font-semibold text-primary">{val}</span>
                              </td>
                            );
                          }
                          if (col === 'name') {
                            return (
                              <td key={col} className={`py-2.5 px-3 text-textSecondary truncate ${def.width}`}>{val || '—'}</td>
                            );
                          }
                          if (col === 'sector' || col === 'industry') {
                            return (
                              <td key={col} className={`py-2.5 px-3 text-textSecondary truncate ${def.width}`}>{val || '—'}</td>
                            );
                          }

                          const formatted = def.format ? def.format(val) : (val ?? '—');
                          const colorCls = def.color ? colorForChange(val) : '';

                          return (
                            <td key={col} className={`py-2.5 px-3 whitespace-nowrap ${def.align === 'right' ? 'text-right' : 'text-left'} ${def.mono ? 'font-mono' : ''} ${colorCls}`}>
                              {formatted}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {!loading && totalCount > 0 && (
              <div className="flex items-center justify-between p-3 border-t border-borderDefault">
                <p className="text-sm text-textSecondary">
                  Showing {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, totalCount)} of {totalCount.toLocaleString()}
                </p>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let p;
                    if (totalPages <= 5) p = i + 1;
                    else if (page <= 3) p = i + 1;
                    else if (page >= totalPages - 2) p = totalPages - 4 + i;
                    else p = page - 2 + i;
                    return (
                      <button key={p} onClick={() => setPage(p)}
                        className={`w-8 h-8 text-sm rounded-md transition ${page === p ? 'bg-primary text-textAccent' : 'text-textSecondary hover:bg-primary/10'}`}>
                        {p}
                      </button>
                    );
                  })}
                  <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel tabKey="visuals" activeTab={activeViewTab}>
        <SectorVisuals
          data={sectorAnalytics}
          loading={analyticsLoading}
          error={analyticsError}
          period={visualPeriod}
          onPeriodChange={setVisualPeriod}
          onRetry={fetchSectorAnalytics}
        />
      </TabPanel>
    </div>
  );
}
